import styled from "@emotion/styled";
import { NATIVE_SOL } from "@stepfinance/common";
import type { NFT } from "@stepfinance/nft";
import { Breakpoints, DEFAULT_EMPTY_CELL, transparentWhite } from "@stepfinance/react";
import { Tooltip } from "antd";
import type { CheckboxValueType } from "antd/lib/checkbox/Group";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { ReactComponent as AssetBreakdownIcon } from "../../../assets/asset-breakdown-icon.svg";
import NFTGalleryIcon from "../../../assets/nft-gallery.svg";
import NoNFTsIcon from "../../../assets/no-nfts-icon.svg";
import { useAppConfig } from "../../../contexts/app/config";
import { useLanguageContext } from "../../../contexts/language/language";
import { useMarkets } from "../../../contexts/market";
import { useNFTContext } from "../../../contexts/nft";
import { useCalculatePriceForDisplayCurrency } from "../../../hooks";
import { useNFTsWithTokensAndPricesByOwner } from "../../../hooks/queries/nft/useNFTsWithTokensAndPrices";
import { useNFTStaking } from "../../../hooks/staking/useNFTStaking";
import { fetchAndLoadNFTOffChainData } from "../../../utils/nft/load";
import { formatSolNFTPrice } from "../../../utils/nft/number-utils";
import { paginationFromWidth } from "../../../utils/nft/utils";
import { isPaginationHidden, slicePages } from "../../../utils/utils";
import { MonospacedText, PAGE_SIZE_OPTIONS } from "../../shared";
import CellValue from "../../Table/CellValue";
import { FilterableNFTCardGrid } from "../FilterableNFTCardGrid";
import { NFTDetailView } from "../NFTDetailView/NFTDetailView";
import { NFTLightbox } from "../NFTLightbox";
import { SelectAndShareTwitterRow } from "./SelectAndShareTwitterRow";

export interface GalleryPaginationConfig {
  hideOnSinglePage: boolean;
  pageSizeOptions: string[];
  pageSize: number;
  page: number;
}

const PAGE_SIZES = PAGE_SIZE_OPTIONS.l;

/**
 * Presents a view of the user's collection (FilterableNFTCardGrid) as well as
 * individual detail views of a selected NFT (NFTDetailView)
 */
export const NFTGallery: React.FC = () => {
  const { userNFTs, setUserNFTs, NFTBalance } = useNFTContext();
  const { status: nftsWithPricesStatus } = useNFTsWithTokensAndPricesByOwner();
  const { hideableValueWrapper } = useAppConfig();
  const { nftPositions: stakedNFTs } = useNFTStaking();
  const calculatePriceForDisplayCurrency = useCalculatePriceForDisplayCurrency();
  const { priceInUSD } = useMarkets();
  const { nftMint } = useParams();
  const {
    currentLanguageData: {
      pages: { nftGallery },
    },
  } = useLanguageContext();

  const [viewingNFT, setViewingNFT] = useState<NFT | undefined>(undefined);
  const [selectedCollections, setSelectedCollections] = useState<CheckboxValueType[]>([]);
  const [showBalances, setShowBalances] = useState(false);

  const [pagination, setPagination] = useState(() => {
    const { innerWidth: width } = window;
    const pagination = {
      hideOnSinglePage: isPaginationHidden(userNFTs.size, PAGE_SIZES),
      pageSizeOptions: PAGE_SIZES as string[],
      pageSize: parseInt(PAGE_SIZES[0]),
      page: 1,
    };
    return paginationFromWidth(width, pagination, PAGE_SIZES);
  });

  const focusedNFTRef = useRef<HTMLImageElement>(null);
  const solPriceUSD = useMemo(() => priceInUSD(NATIVE_SOL.toBase58()), [priceInUSD]);

  useEffect(() => {
    const handleResize = () => {
      const { innerWidth: width } = window;
      setPagination((pagination) => {
        return paginationFromWidth(width, pagination, PAGE_SIZES);
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const filteredNFTs: NFT[] = useMemo(() => {
    const nfts = [...userNFTs.values(), ...stakedNFTs.map((i) => i.nft)].sort((a, b) => {
      if (a.floorPrice || b.floorPrice) {
        return (b.floorPrice ?? 0) - (a.floorPrice ?? 0);
      }
      const byCollection = a.collectionName.localeCompare(b.collectionName);
      if (byCollection !== 0) {
        return byCollection;
      } else {
        return a.name.localeCompare(b.name, undefined, { numeric: true });
      }
    });
    if (selectedCollections.length > 0) {
      const selectedSet = new Set(selectedCollections);
      return nfts.filter((nft) => selectedSet.has(nft.collectionName));
    }
    return nfts;
  }, [selectedCollections, stakedNFTs, userNFTs]);

  const focusedNFT = useMemo(() => {
    // Set focused NFT based on dynamic param.
    if (nftMint) {
      return filteredNFTs.find((data) => data.address.toBase58() === nftMint);
    }
  }, [filteredNFTs, nftMint]);

  const onPaginationChange = (page: number, pageSize: number) => {
    const nftsToFetch = slicePages(filteredNFTs, pageSize, page, 2);
    fetchAndLoadNFTOffChainData(nftsToFetch, setUserNFTs);

    const nftsOnCurrentPage = nftsToFetch.slice(0, pageSize);
    setShowBalances(nftsOnCurrentPage.some((nft) => nft.amount > 1));

    setPagination({
      ...pagination,
      pageSize,
      page,
    });
  };

  const onApplyFilters = useCallback(
    (selectedKeys: React.Key[]) => {
      const selectedSet = new Set(selectedKeys);
      const nfts = filteredNFTs.filter((nft) => selectedSet.has(nft.collectionName));
      const nftsToFetch = slicePages(nfts, pagination.pageSize, pagination.page, 1);
      fetchAndLoadNFTOffChainData(nftsToFetch, setUserNFTs);

      const nftsOnCurrentPage = nftsToFetch.slice(0, pagination.pageSize);
      setShowBalances(nftsOnCurrentPage.some((nft) => nft.amount > 1));

      setSelectedCollections(selectedKeys);
      setPagination({
        ...pagination,
        page: 1,
      });
    },
    [filteredNFTs, pagination, setUserNFTs],
  );

  return (
    <NFTGalleryContainer>
      <div style={{ width: "100%" }}>
        {focusedNFT && <NFTDetailView focusedNFT={focusedNFT} onImageClick={(_, nft: NFT) => setViewingNFT(nft)} />}
        {userNFTs.size > 0 && !focusedNFT && (
          <>
            <TitleRow>
              <h2>
                <img src={NFTGalleryIcon} alt="icon" />
                {nftGallery.title}
              </h2>
              <PriceHeader>
                {NFTBalance > 0 ? (
                  <>
                    <MonospacedText>
                      {hideableValueWrapper(
                        solPriceUSD ? `${formatSolNFTPrice(NFTBalance / solPriceUSD)} SOL` : DEFAULT_EMPTY_CELL,
                      )}
                    </MonospacedText>
                    <Tooltip
                      title={
                        <CellValue cellType="userPositionValue" fontWeight="bold">
                          {NFTBalance}
                        </CellValue>
                      }
                      placement="bottom"
                    >
                      <StyledAssetBreakdownIcon />
                    </Tooltip>
                  </>
                ) : (
                  <MonospacedText>{calculatePriceForDisplayCurrency(NFTBalance, { hideable: false })}</MonospacedText>
                )}
              </PriceHeader>
            </TitleRow>
            <SelectAndShareTwitterRow selectedCollections={selectedCollections} onApplyFilters={onApplyFilters} />
            <FilterableNFTCardGrid
              dataSource={filteredNFTs}
              focusedNFTRef={focusedNFTRef}
              pagination={pagination}
              onPaginationChange={onPaginationChange}
              showBalances={showBalances}
              setShowBalances={setShowBalances}
              onImageClick={(_, nft: NFT) => setViewingNFT(nft)}
              viewingNFT={viewingNFT}
            />
          </>
        )}
        {userNFTs.size === 0 && !focusedNFT && (
          <div className="no-nfts-container">
            <img src={NoNFTsIcon} alt="No NFT's icon" />
            {nftsWithPricesStatus === "loading" ? (
              nftMint ? (
                <span>{nftGallery.searchingForSingleNFT}</span>
              ) : (
                <span>{nftGallery.searchingForNFTs}</span>
              )
            ) : (
              <span>{nftGallery.noNFTsToView}</span>
            )}
          </div>
        )}
      </div>
      {viewingNFT && <NFTLightbox focusedNFTRef={focusedNFTRef} viewingNFT={viewingNFT} />}
    </NFTGalleryContainer>
  );
};

export const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;

  @media (max-width: ${Breakpoints.S}px) {
    flex-direction: column;
  }

  h2 {
    font-size: 28px;
    color: #ffffff;
    &:first-of-type {
      font-weight: 800;
      img {
        width: 27px;
        height: 27px;
        margin-right: 10px;
      }
    }
    &:last-of-type {
      font-weight: normal;
      span {
        font-weight: 800;
      }
    }
  }
`;

const NFTGalleryContainer = styled.div`
  .image-container {
    width: 100%;
    display: flex;

    img {
      max-width: 100%;
      max-height: inherit;
      margin: auto;
    }
  }

  .no-nfts-container {
    height: calc(100vh - calc(2 * 75px));
    display: flex;
    flex-direction: column;

    @media (max-width: 992px) {
      height: calc(100vh - calc(2 * (75px + 48px + 14px)));
    }

    > img {
      margin: auto auto 40px auto;
    }

    > span {
      margin: 0 auto auto auto;
      font-size: 20px;
      line-height: 26px;
    }
  }

  .upper-corners-rounded {
    border-top-right-radius: 8px !important;
    border-top-left-radius: 8px !important;
  }

  .lower-corners-rounded {
    border-bottom-right-radius: 8px !important;
    border-bottom-left-radius: 8px !important;
  }
`;

const PriceHeader = styled.h2`
  @media (max-width: ${Breakpoints.S}px) {
    align-self: center;
  }
`;

const StyledAssetBreakdownIcon = styled(AssetBreakdownIcon)`
  margin-left: 10px;
  height: 16px;
  width: 16px;
  path {
    fill: ${transparentWhite};
  }
`;
